import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { Spinner } from "@ui-kit/feedback/Spinner/Spinner.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import { useTranslation } from "@shared/utils/internationalization";
import Input from "@ui-kit/form/Input/Input.web";
import FormControl, {
  FormErrorMessage,
} from "@ui-kit/form/FormControl/FormControl.web";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import { FormEvent, HtmlHTMLAttributes, useCallback } from "react";
import type { FormSharedProps } from "./Form.props";
import {
  IncrementerProps,
  useFormIncrementor,
} from "./hooks/useFormIncrementor";

const INCREMENTER_CONTROL_SIZE_MAP: Record<string, string> = {
  xs: "element.xl",
  sm: "element.2xl",
  md: "element.3xl",
  lg: "element.4xl",
};

export type FormProps = FormSharedProps & {
  // Add any web specific props here.
};

/**
 * A form that by default prevents the default submit action in favour
 * of calling the `onSubmit` prop.
 */
export default function Form({
  onSubmit,
  ...props
}: HtmlHTMLAttributes<HTMLFormElement> & FormProps) {
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit?.(event);
    },
    [onSubmit]
  );

  return <form onSubmit={handleSubmit} {...props} />;
}

Form.Incrementer = function FormIncrementer({
  incrementerVariant = "default",
  ...props
}: IncrementerProps) {
  if (incrementerVariant === "input") {
    return <Form.IncrementerInput {...props} />;
  }

  return <Form.IncrementerDefault {...props} />;
};

/**
 * A field for incrementing and decrementing a value.
 * Also allows a user a field to input a value directly.
 * Shows 2 buttons on either side of the input field.
 */
Form.IncrementerDefault = function FormIncrementerDefault({
  value,
  onChange,
  size = "sm",
  colorPalette,
  variant = "solid",
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  isDisabled = false,
  ...props
}: IncrementerProps) {
  const { t } = useTranslation("common");
  const {
    isDisabledMin,
    isDisabledMax,
    isDisabledInput,
    amountRequested,
    hasError,
    onDecrement,
    onIncrement,
    onInput,
    onKeyDown,
  } = useFormIncrementor({ onChange, value, min, max, isDisabled });
  const controlSizeToken =
    INCREMENTER_CONTROL_SIZE_MAP[size ?? "sm"] ??
    INCREMENTER_CONTROL_SIZE_MAP.sm;

  // Extract layout props for HStack, rest are for child components
  const { isLoading, ...layoutProps } = props;

  return (
    <FormControl isInvalid={hasError}>
      <HStack
        minWidth="element.9xl"
        {...(layoutProps as Record<string, unknown>)}
      >
        <Button
          data-testid="quantity-incrementor-decrement"
          aria-label={t("Decrement item quantity")}
          size={size}
          variant={isDisabledMin ? "outline" : variant}
          onClick={onDecrement}
          colorPalette={colorPalette}
          disabled={isDisabledMin}
          style={{
            aspectRatio: "1",
            borderRadius: "100%",
          }}
          p="0"
          minW={controlSizeToken}
          w={controlSizeToken}
          h={controlSizeToken}
          _disabled={{
            opacity: 1,
            color: "gray.300",
            pointerEvents: "none",
            borderColor: "gray.300",
          }}
        >
          <Icon
            name="Minus"
            boxSize="element.md"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            colorPalette={(isDisabledMin ? colorPalette : "white") as any}
          />
        </Button>
        {isLoading ? (
          <Flex width="100%" minWidth="element.5xl" justifyContent="center">
            <Spinner />
          </Flex>
        ) : (
          <Input
            data-testid="quantity-incrementor-input"
            aria-label={t("Enter a quantity")}
            isDisabled={isDisabledInput}
            min={min}
            max={max}
            bg="bg"
            size={size}
            type="number"
            onKeyDown={onKeyDown}
            onInput={onInput}
            value={Number(amountRequested).toString()}
            textAlign="center"
            borderColor="gray.300"
            inputMode="numeric"
            pattern="\d"
            isInvalid={hasError}
          />
        )}

        <Button
          data-testid="quantity-incrementor-increment"
          aria-label={t("Increment item quantity")}
          size={size}
          variant={isDisabledMax ? "outline" : variant}
          onClick={onIncrement}
          colorPalette={colorPalette}
          disabled={isDisabledMax}
          style={{
            aspectRatio: "1",
            borderRadius: "100%",
          }}
          p="0"
          minW={controlSizeToken}
          w={controlSizeToken}
          h={controlSizeToken}
          _disabled={{
            opacity: 1,
            color: "gray.300",
            pointerEvents: "none",
            borderColor: "gray.300",
          }}
        >
          <Icon
            name="Plus"
            boxSize="element.md"
            primary={isDisabledMax ? undefined : "white"}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            colorPalette={(isDisabledMax ? colorPalette : undefined) as any}
          />
        </Button>
      </HStack>
      <FormErrorMessage data-testid="quantity-incrementor-error">
        {value < Number(min) &&
          t("Minimum quantity is {{ min }}", { min: Number(min) })}
        {value > Number(max) &&
          t("Maximum quantity is {{ max }}", { max: Number(max) })}
      </FormErrorMessage>
    </FormControl>
  );
};

/**
 * A field for incrementing and decrementing a value.
 * Also allows a user a field to input a value directly.
 * Shows 2 buttons as part of the input field.
 */
Form.IncrementerInput = function FormIncrementerInput({
  value,
  onChange,
  size = "sm",
  colorPalette,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  isDisabled = false,
  ...props
}: IncrementerProps) {
  const { t } = useTranslation("common");

  const {
    isDisabledMin,
    isDisabledMax,
    isDisabledInput,
    amountRequested,
    hasError,
    onDecrement,
    onIncrement,
    onInput,
    onKeyDown,
  } = useFormIncrementor({ onChange, value, min, max, isDisabled });

  // Extract layout props for HStack, rest are for child components
  const { isLoading, ...layoutProps } = props;

  return (
    <FormControl isInvalid={hasError}>
      <HStack
        minWidth="element.9xl"
        {...(layoutProps as Record<string, unknown>)}
        border="1px solid"
        borderColor="gray.300"
        borderRadius="sm"
        backgroundColor="bg"
      >
        <IconButton
          variant="plain"
          data-testid="quantity-incrementor-decrement"
          aria-label={t("Decrement item quantity")}
          size={size}
          onClick={onDecrement}
          colorPalette={colorPalette}
          isDisabled={isDisabledMin}
          iconName="Minus"
          iconColor="fg"
          _hover={{ backgroundColor: "transparent" }}
          _active={{ backgroundColor: "transparent" }}
        />
        {isLoading ? (
          <Flex width="100%" justifyContent="center">
            <Spinner color="gray.400" />
          </Flex>
        ) : (
          <Input
            data-testid="quantity-incrementor-input"
            aria-label={t("Enter a quantity")}
            isDisabled={isDisabledInput}
            border="none"
            min={min}
            max={max}
            bg="bg"
            size={size}
            type="number"
            onKeyDown={onKeyDown}
            onInput={onInput}
            value={Number(amountRequested).toString()}
            textAlign="center"
            inputMode="numeric"
            pattern="\d"
            _focus={{
              border: "none",
              outline: "none",
            }}
            padding={0}
          />
        )}
        <IconButton
          data-testid="quantity-incrementor-increment"
          aria-label={t("Increment item quantity")}
          size={size}
          onClick={onIncrement}
          colorPalette={colorPalette}
          isDisabled={isDisabledMax}
          iconName="Plus"
          iconColor="fg"
          variant="plain"
          _hover={{ backgroundColor: "transparent" }}
          _active={{ backgroundColor: "transparent" }}
        />
      </HStack>
      <FormErrorMessage data-testid="quantity-incrementor-error">
        {value < Number(min) &&
          t("Minimum quantity is {{ min }}", { min: Number(min) })}
        {value > Number(max) &&
          t("Maximum quantity is {{ max }}", { max: Number(max) })}
      </FormErrorMessage>
    </FormControl>
  );
};
